// @spec IAM-STR-008
// @spec IAM-STR-009
// @spec SHL-STR-005
// @spec ANL-STR-018
'use client';

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  OrganizationList,
  ProfileCard,
  ProfileEditForm,
  useProfile,
  useUpdateProfile,
} from '@/features/profile';
import type { ProfileFormData } from '@/features/profile/schemas/profile-form';
import { useApiError } from '@/hooks/use-api-error';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/settings/profile')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.profile'),
  }),
  component: ProfilePage,
});

/**
 * Profile page displaying user information and organization memberships.
 *
 * Shows ProfileCard with user details and OrganizationList with
 * all organizations the user belongs to. Includes edit mode for
 * updating profile information. Handles loading and error states.
 */
function ProfilePage() {
  const { t } = useTranslation('settings');
  const { data: profile, isLoading, error, refetch } = useProfile();
  const updateProfile = useUpdateProfile();
  const apiError = useApiError(updateProfile.error);
  const [isEditing, setIsEditing] = useState(false);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleEditSuccess = () => {
    setIsEditing(false);
  };

  const handleSubmit = async (data: ProfileFormData) => {
    await updateProfile.mutateAsync(data);
  };

  return (
    <div className="p-8">
      <div className="mx-auto max-w-2xl space-y-6">
        <header className="flex items-center justify-between">
          <h1 className="text-3xl font-semibold text-foreground">{t('profile.title')}</h1>
          {!isEditing && profile && <Button onClick={handleEditClick}>{t('profile.editProfile')}</Button>}
        </header>

        {error ? (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
            <p className="text-sm text-destructive mb-4">
              {error instanceof Error ? error.message : t('profile.failedToLoad')}
            </p>
            <Button variant="outline" onClick={() => void refetch()}>
              {t('profile.tryAgain')}
            </Button>
          </div>
        ) : isEditing && profile ? (
          <ProfileEditForm
            user={profile}
            onSuccess={handleEditSuccess}
            onCancel={handleCancelEdit}
            onSubmit={handleSubmit}
            isPending={updateProfile.isPending}
            error={apiError.isApiError ? apiError.displayMessage : undefined}
          />
        ) : (
          <>
            <ProfileCard user={profile} isLoading={isLoading} />
            <OrganizationList organizations={profile?.organizations} isLoading={isLoading} />

          </>
        )}
      </div>
    </div>
  );
}
